import React, { useState } from "react";
import { Loader2, Tag, X } from "lucide-react";
import { useCart } from "@/context/CartContext";

/**
 * DiscountCodeInput — shared UI for both CartDrawer and the Checkout
 * order summary. Reads discount state from CartContext so the code
 * persists across cart drawer <-> checkout without prop drilling.
 *
 * Layout modes:
 *   - variant="drawer"   compact single row (used inside the cart drawer)
 *   - variant="checkout" slightly larger, matches the checkout summary
 */
export default function DiscountCodeInput({ variant = "drawer" }) {
  const {
    discountCode,
    discount,
    discountLoading,
    discountError,
    applyDiscount,
    removeDiscount,
    items,
  } = useCart();
  const [input, setInput] = useState("");

  const applied = !!discount;
  const disabled = items.length === 0;

  const submit = async () => {
    if (!input.trim()) return;
    const res = await applyDiscount(input);
    if (res && res.ok) setInput("");
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  const box =
    variant === "checkout"
      ? "px-4 py-3 border-t-2 border-[#111] bg-[var(--st-cream)]"
      : "px-5 py-3 border-t-2 border-[#111] bg-[var(--st-bg)]";

  if (applied) {
    return (
      <div
        data-testid="discount-applied"
        className={`${box} flex items-center justify-between gap-3`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Tag size={14} className="text-[var(--st-maroon)] shrink-0" />
          <div className="min-w-0">
            <p
              data-testid="discount-applied-code"
              className="font-heading uppercase tracking-[0.18em] text-xs text-[var(--st-fg)] truncate"
            >
              {discount.code}
              <span className="text-[var(--st-maroon)]"> · {discount.label}</span>
            </p>
            <p className="font-body text-[11px] text-[var(--st-fg)]/60">
              You save ${discount.total_savings.toFixed(2)}
              {discount.free_shipping ? " · free shipping" : ""}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={removeDiscount}
          data-testid="discount-remove"
          aria-label="Remove discount"
          className="shrink-0 inline-flex items-center gap-1 border-2 border-[#111] px-2 py-1 font-heading uppercase tracking-[0.18em] text-[10px] hover:bg-[var(--st-maroon)] hover:text-white transition-colors"
        >
          <X size={11} /> Remove
        </button>
      </div>
    );
  }

  return (
    <div className={box}>
      <label
        htmlFor={`discount-input-${variant}`}
        className="flex items-center gap-1.5 font-heading uppercase tracking-[0.2em] text-[10px] md:text-xs text-[var(--st-fg)]/70 mb-1.5"
      >
        <Tag size={11} /> Have a discount code?
      </label>
      <div className="flex gap-2">
        <input
          id={`discount-input-${variant}`}
          data-testid="discount-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={disabled || discountLoading}
          placeholder="Enter code"
          autoComplete="off"
          spellCheck={false}
          className="flex-1 border-2 border-[#111] bg-white px-3 py-2 font-heading uppercase tracking-[0.15em] text-xs text-[var(--st-fg)] outline-none focus:border-[var(--st-maroon)] transition-colors disabled:opacity-50"
        />
        <button
          type="button"
          onClick={submit}
          disabled={disabled || discountLoading || !input.trim()}
          data-testid="discount-apply"
          className="inline-flex items-center gap-1.5 bg-[var(--st-maroon)] text-white border-2 border-[#111] px-3.5 py-2 font-heading uppercase tracking-[0.18em] text-xs hover:bg-[var(--st-mustard)] hover:text-[var(--st-fg)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-brutal-sm"
        >
          {discountLoading ? <Loader2 size={12} className="animate-spin" /> : null}
          Apply
        </button>
      </div>
      {discountError && (
        <p
          data-testid="discount-error"
          className="mt-1.5 font-body text-[11px] text-[var(--st-maroon)]"
        >
          {discountError}
        </p>
      )}
      {!discountError && discountCode && !applied && !discountLoading && (
        <p className="mt-1.5 font-body text-[11px] text-[var(--st-fg)]/60">
          Saved code {discountCode} — add items to activate it.
        </p>
      )}
    </div>
  );
}
